import { Button, Typography } from '@amway/react-components';
import { AxiosError } from 'axios';
import moment from 'moment';
import { useState } from 'react';
import { Card, Container, Form, Row, Spinner } from 'react-bootstrap';
import DatePickerComponent from '../../components/shared/datepicker';
import AlertComponent from '../../components/ui/alert';
import reportService from '../../service/report.service';
import { toISOString } from '../../utils/date-utils';
import './index.scss';

const currentDay = moment().format('YYYY-MM-DD');
const firstMonthDay = moment().startOf('month').format('YYYY-MM-DD');

export default function FeedbackReportComponent() {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>(firstMonthDay);
  const [endDate, setEndDate] = useState<string>(currentDay);
  const [reportFormat, setReportFormat] = useState<'csv' | 'html'>('html');
  const [reportType, setReportType] = useState<'all' | 'positive' | 'negative'>('negative');
  const [minDate] = useState<string>(new Date('2024-09-01').toISOString());
  const [maxDate] = useState<string>(toISOString(new Date()));

  function downloadReport() {
    setErrorMessage('');
    setSuccessMessage('');
    setIsDownloading(true);

    if (!startDate || !endDate) {
      setErrorMessage('Start Date and End Date are required');
      setIsDownloading(false);
      return;
    }

    if (moment(startDate).isAfter(endDate)) {
      setErrorMessage('Start Date cannot be later than End Date.');
      setIsDownloading(false);
      return;
    }

    if (moment(endDate).diff(moment(startDate), 'days') > 365) {
      setErrorMessage('The date range cannot exceed 365 days.');
      setIsDownloading(false);
      return;
    }

    reportService
      .getReport(startDate, endDate, reportFormat, reportType)
      .promise.then((res: any) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `feedback_report_${startDate}_${endDate}.${reportFormat}`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        setSuccessMessage('Report downloaded successfully.');
      })
      .catch((error: AxiosError) => {
        console.error('Error downloading report.', error);
        setErrorMessage('Error downloading report. Please try again.');
      })
      .finally(() => setIsDownloading(false));
  }

  return (
    <Container className="feedback-report">
      <Card>
        <Card.Body>
          <Typography variant="heading">Feedback Report</Typography>
          {errorMessage && (
            <AlertComponent text={errorMessage} type="danger" onClick={() => setErrorMessage('')} buttonText="X" />
          )}
          {successMessage && (
            <AlertComponent text={successMessage} type="success" onClick={() => setSuccessMessage('')} buttonText="X" />
          )}
          <Row>
            <DatePickerComponent
              id="startDate"
              className="date-picker"
              label="Start date"
              onChange={setStartDate}
              value={startDate}
              minDate={minDate}
              maxDate={endDate}
            />
            <DatePickerComponent
              id="endDate"
              className="date-picker"
              label="End date"
              onChange={setEndDate}
              value={endDate}
              minDate={startDate}
              maxDate={maxDate}
            />
            <Form.Group className="date-picker" controlId="reportFormat" style={{ minWidth: '150px' }}>
              <Form.Label>File format</Form.Label>
              <Form.Select value={reportFormat} onChange={e => setReportFormat(e.target.value as 'csv' | 'html')}>
                <option value="csv">CSV</option>
                <option value="html">HTML</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="date-picker" controlId="reportType" style={{ minWidth: '150px' }}>
              <Form.Label>Feedback type</Form.Label>
              <Form.Select
                value={reportType}
                onChange={e => setReportType(e.target.value as 'all' | 'positive' | 'negative')}>
                <option value="all">All</option>
                <option value="positive">Positive</option>
                <option value="negative">Negative</option>
              </Form.Select>
            </Form.Group>
          </Row>
          <Row>
            <Button className="feedback-report-download-button" onClick={downloadReport} disabled={isDownloading}>
              {isDownloading ? <Spinner animation="border" size="sm" /> : 'Download Report'}
            </Button>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
}
